import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Chip, Link, Stack } from '@mui/material';
import { CasRHFAutocomplete } from 'src/compass/shared/components';
import { useRetrieveEntityList } from 'src/compass/shared/hooks/reactQuery/useRetrieveEntityList';

import {
  RecipientOptionType,
  SendChannelType,
  channelFirst,
  channelTag,
  recipientLabel,
  supportsChannel,
} from './composeMode';
// By path: this file is inside the application barrel, so importing through it would be a cycle.
import { CONTACT_PHONE_COLUMNS } from '../entityMaintenance/CustomerContacts/contactPhones';

const CHANNEL_CHIP_SX = { textTransform: 'uppercase', fontSize: 10 } as const;

interface Props {
  /*
    Where the address pick-list comes from. The two senders read different lists of the same
    shape - SCE Reports a grower's contacts (SceContact/Email/Select), Customers a customer's
    (Utilities/CustomerMessage/Email/Select) - so the route is the caller's to name rather than
    something this field decides from which id it was handed.
  */
  optionsEntity: string;
  optionsParams: Record<string, string | number | undefined>;
  update: Date | null;
  selectedTo: RecipientOptionType[];
  selectedCC: RecipientOptionType[];
  channel: SendChannelType;
  /* False while Auto is still waiting on a non-employee to settle the channel. */
  channelLocked: boolean;
  /* A text has no real CC, so it is only kept on screen while it still holds someone. */
  showCC: boolean;
}

export function RHFMessageRecipients({
  optionsEntity,
  optionsParams,
  update,
  selectedTo = [],
  selectedCC = [],
  channel,
  channelLocked,
  showCC,
}: Props) {
  const { setValue, control } = useFormContext();

  // CC is empty on most sends, so it starts collapsed behind a "Show CC" link rather than
  // spending a full field's height on an empty input. Once revealed it stays revealed, even if
  // the user then clears every recipient out of it again.
  const [ccRevealed, setCcRevealed] = useState(false);

  const currentCc = useWatch({ control, name: 'cc' });
  const currentTo = useWatch({ control, name: 'to' });

  const { data, isFetching, refetch } = useRetrieveEntityList({
    entity: optionsEntity,
    params: optionsParams,
    // Nothing to ask for until the caller knows whose list it wants - an id still resolving would
    // otherwise fetch the whole table's worth of employees under a missing filter.
    enabled: Object.values(optionsParams).every((value) => value !== undefined && value !== ''),
  });

  /*
    Before paint, not after: this is form state catching up with its props, and a painted frame
    where the field still holds the previous row's addresses is a frame where anything reading the
    field - the channel it resolves to, the badge beside it - describes the wrong row.
  */
  useLayoutEffect(() => {
    setValue('to', selectedTo);
    setValue('cc', selectedCC);
  }, [setValue, selectedTo, selectedCC]);

  useEffect(() => {
    if(update) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update])

  const options = useMemo(() => {
    const selectionList = data?.result ?? [];

    if (!selectionList.length) return [];

    const selectedIdentifiers = new Set<string>();

    (currentCc ?? []).forEach((item: RecipientOptionType) => selectedIdentifiers.add(item.id));
    (currentTo ?? []).forEach((item: RecipientOptionType) => selectedIdentifiers.add(item.id));

    const unselected = selectionList.filter(
      (option: RecipientOptionType) => !selectedIdentifiers.has(option.id)
    );

    // Until the channel is settled there is no "current" one to put first, so the server's order -
    // the sender's own first channel - stands.
    return channelLocked ? channelFirst(unselected, channel) : unselected;
  }, [data?.result, currentCc, currentTo, channel, channelLocked]);

  /*
    Once the channel is settled, contacts for the other one stay in the list but
    are not selectable, so it is clear the contact exists and why it cannot be
    used right now. Until then everything is fair game.
  */
  const isDisabled = (option: RecipientOptionType) =>
    channelLocked && !supportsChannel(option, channel);

  /*
    A customer contact's number says which column it came from, in the words the Contacts tab
    uses (Mobile / Phone), since one contact can be listed under two numbers. The address their
    Preferred Channel names is bold, with a Preferred chip ahead of the channel chip.
  */
  const renderOption = (liProps: object, option: RecipientOptionType) => {
    const tag = channelTag(option);
    // Guarded: Business_phone's column has a null channel, which every non-phone option would match.
    const phoneSuffix =
      option.phoneField &&
      CONTACT_PHONE_COLUMNS.find(({ channel: column }) => column === option.phoneField)?.suffix;

    return (
      <li {...liProps} key={option.id}>
        <Stack direction="row" alignItems="center" spacing={1} width="100%">
          <Box component="span" sx={{ fontWeight: option.isPreferred ? 'bold' : undefined, mr: 'auto' }}>
            {recipientLabel(option, channel)}
            {phoneSuffix && (
              <>
                {' '}
                <i>{phoneSuffix}</i>
              </>
            )}
          </Box>
          {option.isPreferred && (
            <Chip
              size="small"
              label="Preferred"
              // Pure yellow, the app's "look here" colour; the theme's warning palette is amber.
              sx={{
                ...CHANNEL_CHIP_SX,
                color: 'text.primary',
                backgroundColor: 'yellow',
                '&:hover': { backgroundColor: 'yellow' },
              }}
            />
          )}
          <Chip size="small" variant="soft" color={tag.color} label={tag.label} sx={CHANNEL_CHIP_SX} />
        </Stack>
      </li>
    );
  };

  // Two separate questions, and CC needs a yes to both. showCC is the mode's: a text has no CC of
  // its own, so SMS drops it entirely while it is empty. ccOpen is the sender's: within a mode
  // that does allow CC, it stays collapsed behind the link until they ask for it.
  const ccOpen = ccRevealed || (currentCc?.length ?? 0) > 0;

  const sharedProps = {
    multiple: true as const,
    loading: isFetching,
    options,
    optionsNameField: (option: RecipientOptionType) => recipientLabel(option, channel),
    getOptionDisabled: isDisabled,
    renderOption,
    // A recipient is a neutral token, not a status badge. The theme styles a filled default Chip
    // as white-on-text.primary (near black - see theme/overrides/components/chip.tsx), which is
    // far too stark for a list of email addresses; "soft" is the theme's own quiet grey pill.
    ChipProps: { variant: 'soft' as const },
  };

  return (
    <>
      <CasRHFAutocomplete {...sharedProps} name="to" label="To" />
      {showCC &&
        (ccOpen ? (
          <CasRHFAutocomplete {...sharedProps} name="cc" label="CC" />
        ) : (
          // A one-line link should not take a full field's worth of vertical rhythm: the negative
          // top margin eats most of the 12px the To field carries below itself (Input.scss), so
          // the link sits close under it.
          <Box sx={{ mt: '-6px', mb: '6px', pl: '25px' }}>
            <Link
              component="button"
              // The link sits inside the compose form, where a bare <button> would default to
              // type="submit" and send the email.
              type="button"
              variant="body2"
              underline="none"
              onClick={() => setCcRevealed(true)}
            >
              Show CC
            </Link>
          </Box>
        ))}
    </>
  );
}
